import { useEffect, useState } from "react";
import { useFetchers, useNavigation } from "react-router";

export function NavigationProgress() {
  const navigation = useNavigation();
  const fetchers = useFetchers();

  // Check if navigation OR any fetcher is loading
  const isLoading =
    navigation.state === "loading" ||
    fetchers.some((f) => f.state === "loading" || f.state === "submitting");

  const [progress, setProgress] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [transitionDuration, setTransitionDuration] = useState(100);

  useEffect(() => {
    const timers: NodeJS.Timeout[] = [];

    if (isLoading) {
      // Reset state immediately
      setProgress(0);
      setTransitionDuration(0);

      // Wait 100ms before showing progress bar (only show for slow loads)
      timers.push(
        setTimeout(() => {
          setIsVisible(true);

          // Stage 1: Fast to 25% (100ms)
          timers.push(
            setTimeout(() => {
              setTransitionDuration(100);
              setProgress(25);

              // Stage 2: Slow to 40% (800ms)
              timers.push(
                setTimeout(() => {
                  setTransitionDuration(800);
                  setProgress(40);

                  // Stage 3: Fast to 90% (300ms)
                  timers.push(
                    setTimeout(() => {
                      setTransitionDuration(300);
                      setProgress(90);
                    }, 850),
                  );
                }, 150),
              );
            }, 50),
          );
        }, 100),
      );
    } else if (isVisible) {
      // Jump to 100% when loading completes
      setTransitionDuration(200);
      setProgress(100);

      // Hide after fade out
      timers.push(
        setTimeout(() => {
          setIsVisible(false);
          setProgress(0);
        }, 300),
      );
    }

    // Cleanup all timers
    return () => {
      for (const timer of timers) {
        clearTimeout(timer);
      }
    };
  }, [isLoading, isVisible]);

  if (!isVisible) {
    return null;
  }

  return (
    <div
      className="fixed top-0 left-0 right-0 h-1 bg-loamGreen z-progress-bar overflow-hidden"
      style={{
        width: `${progress}%`,
        opacity: progress === 100 ? 0 : 1,
        transition:
          progress === 100
            ? "width 200ms ease-in-out, opacity 300ms ease-in-out"
            : `width ${transitionDuration}ms ease-in-out`,
      }}
    >
      <div
        className="absolute inset-0 bg-linear-to-r from-transparent via-white/60 to-transparent"
        style={{
          animation: "shimmer 2s infinite",
          width: "100%",
        }}
      />
      <style>{`
        @keyframes shimmer {
          0% {
            transform: translateX(-100%);
          }
          75% {
            transform: translateX(100%);
          }
          100% {
            transform: translateX(100%);
          }
        }
      `}</style>
    </div>
  );
}
